import type { ReactElement, ReactNode } from 'react';
import { Box } from '@mui/material';
import { Navigate } from 'react-router-dom';

import useHasPlusFeature from 'app/hooks/useHasPlusFeature';
import type { OrganisationFeature } from 'app/hooks/useOrganisationFeatures';
import useTopHeader from 'app/hooks/useTopHeader';
import CenteredSpinner from 'app/shared-components/common/CenteredSpinner';

import PlanFeaturesErrorState from './PlanFeaturesErrorState';

interface PlusFeatureGateProps {
  feature: OrganisationFeature;
  /** Page header while the paywall is shown; the gated page sets its own once it renders. */
  title: string;
  paywall: ReactNode;
  hasAccessToday?: boolean;
  children: ReactNode;
}

interface PaywallPageProps {
  title: string;
  children: ReactNode;
}

function PaywallPage({ title, children }: PaywallPageProps): ReactElement {
  useTopHeader(title);

  return <Box className="grow overflow-y-auto px-20 py-16 bg-neutral-100">{children}</Box>;
}

export default function PlusFeatureGate({
  feature,
  title,
  paywall,
  hasAccessToday = true,
  children,
}: PlusFeatureGateProps): ReactElement {
  const { hasAccess, isHidden, isError, retry } = useHasPlusFeature(feature, hasAccessToday);

  if (hasAccess) return <>{children}</>;
  // Switched off for this organisation: nothing to upsell, so the page doesn't exist for it.
  if (isHidden) return <Navigate to="/" replace />;

  let content: ReactNode = paywall;
  if (isError) content = <PlanFeaturesErrorState onRetry={retry} />;
  else if (hasAccess === undefined) content = <CenteredSpinner />;

  return <PaywallPage title={title}>{content}</PaywallPage>;
}
